import { useState } from "react";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { apiClient } from "@/lib/api-client";
import { sanitizeCoachInputOrEmpty } from "@/lib/coach-input";

const MAX_REFLECTION_CHARS = 2000;

export type ReflectionEntityType = "objective" | "kr_set";

interface ReflectionDialogProps {
  open: boolean;
  entityType: ReflectionEntityType;
  firstDraft: string;
  finalDraft: string;
  onSubmitted: () => void;
  onSkip: () => void;
}

/**
 * Metacognitive close for an OKR Coach session. Capture one reflective
 * sentence and persist. Failure to submit should never block the wizard —
 * the outer ErrorBoundary + graceful catch preserve the user's draft.
 */
export function ReflectionDialog(props: ReflectionDialogProps) {
  return (
    <Dialog open={props.open} onOpenChange={(o) => !o && props.onSkip()}>
      <DialogContent className="sm:max-w-lg">
        <ErrorBoundary
          fallback={<ReflectionFallback onSkip={props.onSkip} />}
        >
          <ReflectionForm {...props} />
        </ErrorBoundary>
      </DialogContent>
    </Dialog>
  );
}

function ReflectionForm({
  entityType,
  firstDraft,
  finalDraft,
  onSubmitted,
  onSkip,
}: ReflectionDialogProps) {
  const [text, setText] = useState("");
  const mutation = useMutation({
    mutationFn: async (reflection: string) => {
      const { data, error } = await apiClient.POST("/v1/coach/reflection", {
        body: {
          entity_type: entityType,
          first_draft: firstDraft,
          final_draft: finalDraft,
          reflection,
        },
      });
      if (error) throw new Error(error.error.message || "Failed to save reflection.");
      return data;
    },
  });

  const cleaned = sanitize(text);
  const canSubmit = cleaned.length > 0 && !mutation.isPending;

  const submit = () => {
    if (!canSubmit) return;
    mutation.mutate(cleaned, {
      onSuccess: () => {
        onSubmitted();
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : "Reflection failed to save.");
        // Still advance — the draft matters more than the reflection.
        onSubmitted();
      },
    });
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>One last question</DialogTitle>
        <DialogDescription>
          Looking back at your first draft and your final version, what
          changed, and what principle drove the change?
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-2 pt-2">
        <Label htmlFor="coach-reflection" className="sr-only">
          Reflection
        </Label>
        <Textarea
          id="coach-reflection"
          rows={5}
          maxLength={MAX_REFLECTION_CHARS}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="One or two sentences is plenty."
          autoFocus
        />
        <div className="flex justify-end text-xs text-[hsl(var(--muted-foreground))]">
          {text.length} / {MAX_REFLECTION_CHARS}
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onSkip} disabled={mutation.isPending}>
          Skip
        </Button>
        <Button onClick={submit} disabled={!canSubmit}>
          {mutation.isPending ? "Saving…" : "Save & Continue"}
        </Button>
      </DialogFooter>
    </>
  );
}

function ReflectionFallback({ onSkip }: { onSkip: () => void }) {
  return (
    <div className="space-y-4 p-2">
      <DialogHeader>
        <DialogTitle>Couldn't load reflection</DialogTitle>
        <DialogDescription>
          Your draft is safe. Continue without recording a reflection.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button onClick={onSkip}>Continue</Button>
      </DialogFooter>
    </div>
  );
}

const sanitize = (raw: string) =>
  sanitizeCoachInputOrEmpty(raw, MAX_REFLECTION_CHARS);
